import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { useState } from "react";
import { Tooltip } from "primereact/tooltip";
import { isValidPassword } from "../../utils/commonLogics";
import { changePasswordApi } from "../../services/user/manageusers";
import { useSelector } from "react-redux";
import { showToast } from "../../services/toast/toastService";

const ChangePasswordModal = ({ showChangePwdModal, setShowChangePwdModal }) => {
  const { token, user } = useSelector((state) => state.auth);
  const [changePwdForm, setChangePwdForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [changePwdFormErrors, setChangePwdFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordRules = `- Must contain minimum 8 characters
    - Must contain at least 1 uppercase letter
    - Must contain at least 1 lowercase letter
    - Must contain at least 1 special character (! ~ \` @ # $ % ^ & * ( ) < = > ? " + ' / : ; , - { _ } |)
    - Must contain at least 1 number`;

  const passwordRulesArray = passwordRules
    .split("\n") // split by new line
    .map((rule) => rule.trim()) // remove spaces
    .filter((rule) => rule) // remove empty lines
    .map((rule) => rule.replace(/^- /, ""));

  const validateForm = (values) => {
    let errors = {};

    if (!values.current_password) {
      errors.current_password = "Please enter current password correctly.";
      return errors;
    }

    if (
      !values.new_password ||
      values.new_password.length < 8 ||
      !isValidPassword(values.new_password)
    ) {
      errors.new_password = passwordRulesArray;
      return errors;
    }

    if (
      !values.confirm_password ||
      values.confirm_password !== values.new_password
    ) {
      errors.confirm_password =
        "New password and Confirm password do not match.";
    }

    return errors;
  };

  const handleOnSubmit = () => {
    const formErrors = validateForm(changePwdForm);

    setChangePwdFormErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      let payload = {
        current_pwd: changePwdForm?.current_password,
        new_pwd: changePwdForm?.new_password,
        logged_in_user_id: user?.id,
        role: user?.type,
      };
      setIsSubmitting(true);

      changePasswordApi(token, payload)
        .then((response) => {
          if (response?.data?.message) {
            showToast({
              severity: "success",
              summary: "Success",
              detail: response?.data?.message,
              life: 3000,
            });
            setIsSubmitting(false);
            setShowChangePwdModal(!showChangePwdModal);
          } else {
            setIsSubmitting(false);
            showToast({
              severity: "error",
              summary: "Error",
              detail: "Error in password change",
              life: 3000,
            });
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          const errMsg = error?.response?.data?.message;
          if (errMsg === "Incorrect current password") {
            setChangePwdFormErrors({ current_password: errMsg });
          }
          showToast({
            severity: "error",
            summary: "Error",
            detail: errMsg || "Error in password change",
            life: 3000,
          });
        });
    }
  };

  const handleChangePwdChange = (e) => {
    setChangePwdForm((prevProp) => ({
      ...prevProp,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <Dialog
      header="Change Password"
      visible={showChangePwdModal}
      onHide={() => {
        if (!showChangePwdModal) return;
        setShowChangePwdModal(false);
      }}
      style={{ width: "35vw" }}
      closable
      draggable={false}
      resizable={false}
      modal
      blockScroll
    >
      <div className="change-pwd pt-4">
        <div className="current-password-input">
          <label htmlFor={"current-password"} className="dark-text">
            Current Password
          </label>
          <Password
            id="current-password"
            autoFocus
            value={changePwdForm.current_password}
            onChange={(e) => handleChangePwdChange(e)}
            feedback={false}
            name="current_password"
            className={`w-100 password-input mt-1 ${changePwdFormErrors.current_password ? "error-input" : ""}`}
            disabled={isSubmitting}
            style={{
              zIndex: 999,
            }}
          />

          {changePwdFormErrors.current_password && (
            <div className="error-message text-start text-danger align-items-left fs-error mt-2">
              {changePwdFormErrors.current_password}
            </div>
          )}
        </div>

        <div className="new-password-input mt-3">
          <div className="dark-text d-flex align-items-center">
            <span className="pe-2">New Password</span>
            <Tooltip target=".password-field" />
            <div
              className="password-field"
              data-pr-tooltip={passwordRules}
              data-pr-position="right"
              data-pr-html="true"
              placeholder="Enter new password"
              style={{ marginLeft: "8px", cursor: "pointer", marginTop: "0px" }}
            >
              <span className="pi pi-info-circle text-info cursor-pointer"></span>
            </div>
          </div>
          <Password
            id="new-password"
            autoFocus
            value={changePwdForm.new_password}
            onChange={(e) => handleChangePwdChange(e)}
            feedback={false}
            toggleMask
            name="new_password"
            className={`w-100 password-input username change-password mt-1 ${changePwdFormErrors.new_password ? "error-input" : ""}`}
            disabled={isSubmitting}
            style={{
              zIndex: 999,
            }}
          />
        </div>

        <div className="confirm-password-input mt-3">
          <label htmlFor={"new-password"} className="dark-text">
            Confirm Password
          </label>
          <Password
            id="confirm-password"
            autoFocus
            value={changePwdForm.confirm_password}
            onChange={(e) => handleChangePwdChange(e)}
            feedback={false}
            name="confirm_password"
            className={`w-100 password-input mt-1 ${changePwdFormErrors.confirm_password ? "error-input" : ""}`}
            disabled={isSubmitting}
            style={{
              zIndex: 999,
            }}
          />
        </div>

        {Array.isArray(changePwdFormErrors?.new_password) && (
          <div className="error-message text-start text-danger align-items-left fs-error mt-2">
            <div className="fw-600">Password Requirements:</div>
            <div>
              {changePwdFormErrors?.new_password.map((rule, index) => (
                <div key={index}>- {rule}</div>
              ))}
            </div>
          </div>
        )}

        {changePwdFormErrors.confirm_password && (
          <div className="error-message text-start text-danger align-items-left fs-error mt-2">
            {changePwdFormErrors.confirm_password}
          </div>
        )}

        <div className="d-flex justify-content-center gap-2 mt-4">
          <Button
            label="Cancel"
            className="px-4 py-2 fw-bold cancel"
            onClick={() => {
              if (!showChangePwdModal) return;
              setShowChangePwdModal(false);
            }}
          />

          <Button
            label="Submit"
            className="px-4 py-2 fw-bold createClientBtn d-flex align-items-center"
            onClick={handleOnSubmit}
            loading={isSubmitting}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ChangePasswordModal;
